import { createSelector } from "@reduxjs/toolkit";
import type { RootState } from "../../store";
import { collect, contains, distinct, flatten, groupBy } from "helpers/coll";
import {
  AiChatView,
  AiChatWithMessagesView,
  CalcCaseProperty,
  CaseDisplayGroup,
  CellAiConfigView,
  CustomCaseProperty,
  CustomDocumentProperty,
  DiffType,
  FieldAiConfigView,
  FieldResponseStats,
  FieldTagConfigView,
  Form,
  FormField,
  FormPage,
  FormSection,
  Lens,
  LensChronologyConfigView,
  LensOutcomeStatusConfigurationView,
  LensRule,
  LensRuleViewDiff,
  PageLabel,
  PageLabelRule,
  PageLabelStats,
  ReviewConfig,
  SimpleLensView,
  SystemPageLabelGroupView,
  TagColor,
  TagView,
  ValueGroupValidationError,
} from "generated/graphql";
import { LensEditorState, ProfgenBatchState } from ".";
import RequestStatus from "../RequestStatus";

export const getSelectedLensRuleType = (state: LensEditorState) => {
  return state.selectedSubtypes !== undefined && state.selectedDescriptions !== undefined
    ? {
        category: state.selectedCategory,
        subtype: state.selectedSubtypes,
        description: state.selectedDescriptions,
      }
    : undefined;
};

export const selectLensEditorLens = (state: RootState): Lens => state.lensEditor.lens;

export const selectLensEditorLenses = (
  state: RootState
): {
  visibleLenses: SimpleLensView[];
  hiddenLenses: SimpleLensView[];
} => {
  const hiddenLenses = state.lensEditor.lenses.filter((lens) => lens.hidden);
  const visibleLenses = state.lensEditor.lenses.filter((lens) => !lens.hidden);

  return { visibleLenses, hiddenLenses };
};

export const selectLensEditorLensOutcomeStatusConfiguation = (state: RootState): LensOutcomeStatusConfigurationView[] =>
  state.lensEditor.lensOutcomeStatusConfigurations;
export const selectLensEditorLensId = (state: RootState): number => state.lensEditor.lens.id;
export const selectLensEditorSBREnabled = (state: RootState): boolean =>
  state.lensEditor.lens.sectionBasedReviewEnabled ?? false;
export const selectLensEditorForm = (state: RootState): Form => state.lensEditor.form;
export const selectLensEditorFormFields = (state: RootState): FormField[] => state.lensEditor.form.fields;
export const selectLensEditorFormFieldsMap = createSelector(
  [selectLensEditorFormFields],
  (fields) => new Map(fields.map((f) => [f.id, f]))
);
export const selectLensEditorFormSections = (state: RootState): FormSection[] => state.lensEditor.form.sections;
export const selectLensEditorFormSectionsMap = createSelector(
  [selectLensEditorFormSections],
  (sections) => new Map(sections.map((s) => [s.id, s]))
);
export const selectLensEditorFormId = (state: RootState): number => state.lensEditor.form.id;
export const selectLensEditorDraftRules = (state: RootState): LensRule[] => state.lensEditor.draftRules;
export const selectLensEditorLiveRules = (state: RootState): LensRule[] => state.lensEditor.liveRules;
export const selectLensEditorDraftDiff = (state: RootState): LensRuleViewDiff[] => state.lensEditor.draftDiff || [];
export const selectLensEditorRuleSerial = (state: RootState): number => state.lensEditor.ruleIdSerial;
export const selectLensEditorAiChats = (state: RootState): AiChatView[] => state.lensEditor.aiChats;
export const selectLensEditorAiChatWithMessages = (state: RootState): AiChatWithMessagesView | undefined =>
  state.lensEditor.aiChatWithMessages;
export const selectLensEditorFieldAiConfigs = (state: RootState): FieldAiConfigView[] =>
  state.lensEditor.fieldAiConfigs;
export const selectLensEditorFieldTagConfigs = (state: RootState): FieldTagConfigView[] =>
  state.lensEditor.fieldTagConfigs;
export const selectLensEditorCellAiConfigs = (state: RootState): CellAiConfigView[] => state.lensEditor.cellAiConfigs;
export const selectLensEditorTags = (state: RootState): TagView[] => state.lensEditor.tags;
export const selectLensEditorTagColors = (state: RootState): TagColor[] => state.lensEditor.tagColors;

/*** Start Profile Builder */
export const selectProfileBuilderFormResponseStats = (state: RootState): FieldResponseStats[] =>
  state.lensEditor.formResponseStats;
export const selectProfileBuilderFields = (state: RootState): FormField[] => {
  return state.lensEditor.form.fields.slice(0).sort((a, b) => a.rank - b.rank);
};

export const selectProfileBuilderSections = (state: RootState): FormSection[] => {
  return state.lensEditor.form.sections;
};

export const selectProfileBuilderSortedSections = createSelector([selectProfileBuilderSections], (sections) =>
  sections.slice(0).sort((a, b) => a.rank - b.rank)
);

export const selectProfileBuilderSectionlessFields = createSelector([selectProfileBuilderFields], (fields) =>
  fields.filter((f) => !f.sectionId)
);
export const selectLensFormSectionsRaw = createSelector([selectLensEditorForm], (form) => form.sections);
export const selectLensFormSections = createSelector([selectLensFormSectionsRaw], (sections) =>
  sections.slice(0).sort((a, b) => a.rank - b.rank)
);
export const selectLensFormPages = (state: RootState): FormPage[] => state.lensEditor.form.pages;

export const selectProfileBuilderFormResponseErrors = (state: RootState): ValueGroupValidationError[] =>
  state.lensEditor.formResponseErrors;
export const selectProfileBuilderFormResponseErrorsLoading = (state: RootState): RequestStatus =>
  state.lensEditor.formResponseErrorsLoading;
export const selectProfileBuilderFormResponseErrorFieldsUnique = createSelector(
  [selectProfileBuilderFormResponseErrors],
  (errors) => {
    const fields = errors.map((e) => e.fieldId);
    // not sure if distinct is the right helper but.. it works
    return distinct(fields);
  }
);
/*** End Profile Builder */

export const selectDraftMode = createSelector([selectLensEditorLens], (lens) => Boolean(lens.draftVersion));
export const selectRules = createSelector(
  [selectDraftMode, selectLensEditorDraftRules, selectLensEditorLiveRules],
  (draftMode, draft, live) => (draftMode ? draft || [] : live)
);

export const selectSelectedRuleTypeComponents = (state: RootState) => ({
  category: state.lensEditor.selectedCategory,
  subtypes: state.lensEditor.selectedSubtypes,
  descriptions: state.lensEditor.selectedDescriptions,
});
export const selectSelectedLensRuleType = (state: RootState) => getSelectedLensRuleType(state.lensEditor);

export const selectFilteredRules = createSelector([selectRules, selectSelectedLensRuleType], (rules, lensRuleType) => {
  if (lensRuleType) {
    return rules.filter(
      (rule) =>
        rule.category === lensRuleType.category &&
        lensRuleType.subtype.includes(rule.subtype) &&
        lensRuleType.description.includes(rule.description)
    );
  } else {
    return [];
  }
});

export const selectConceptCache = (state: RootState) => state.lensEditor.conceptCache;

const makeSelectDiffsByType = (diffType: LensRuleViewDiff["diffType"]) =>
  createSelector([selectLensEditorDraftDiff], (draftDiff) => (draftDiff || []).filter((d) => d.diffType === diffType));
export const selectRulesAdded = (st: RootState) =>
  flatten(makeSelectDiffsByType(DiffType.Added)(st).map((diff) => diff.newRule));
export const selectRulesRemoved = (st: RootState) =>
  flatten(makeSelectDiffsByType(DiffType.Removed)(st).map((diff) => diff.oldRule));
export const selectRulesQueryChanged = makeSelectDiffsByType(DiffType.QueryChanged);
export const selectRulesMetadataEdit = makeSelectDiffsByType(DiffType.MetadataEdit);

export const selectAllSubtypes = createSelector([selectRules], (rules) =>
  distinct(rules.map((r) => r.subtype)).sort((a, b) => a.localeCompare(b))
);
export const selectAllDescriptions = createSelector([selectRules], (rules) =>
  distinct(rules.map((r) => r.description)).sort((a, b) => a.localeCompare(b))
);

export const selectCurrentField = (state: RootState): FormField | undefined => state.lensEditor.selectedField;

export const selectAllFieldRules = createSelector([selectRules, selectCurrentField], (rules, field) => {
  if (field) {
    return rules.filter((rule) => rule.fieldId === field?.id);
  } else {
    return rules.filter((rule) => !rule.fieldId);
  }
});

export const selectSelectedFieldRules = createSelector(
  [selectAllFieldRules, selectSelectedRuleTypeComponents],
  (rules, { subtypes, descriptions }) =>
    rules.filter((rule) => subtypes.includes(rule.subtype) && descriptions.includes(rule.description))
);

export const selectAllEntityIds = createSelector([selectRules], (rules) => {
  const rulesArr = collect(rules, (r) => r.entityQuery?.entityId);
  return new Set(rulesArr);
});

export const selectMissingEntityIds = createSelector(
  [selectAllEntityIds, selectConceptCache],
  (entityIds, conceptCache) => {
    const missing = new Set(entityIds);
    Object.keys(conceptCache).forEach((id) => {
      missing.delete(id);
    });
    return missing;
  }
);

const getRuleHash = (r: LensRule, query?: string): string => {
  return `${r.fieldId}-${r.category}-${r.subtype}-${r.description}-${query}`;
};

export const selectDuplicateEntities = createSelector([selectRules], (rules: LensRule[]) => {
  const dupes: [string, LensRule][] = [];
  const entityIdSet = new Set();
  rules.forEach((rule) => {
    const entity = rule.entityQuery?.entityId;
    if (entity) {
      const isSeen = entityIdSet.has(entity);
      if (isSeen) dupes.push([entity, rule]);
      else entityIdSet.add(entity);
    }
  });
  return dupes;
});

export const selectDuplicateRules = createSelector([selectRules], (rules: LensRule[]) => {
  const dupes: [LensRule, number][] = [];
  const ruleIdSet = new Set();
  rules.forEach((rule, idx) => {
    const query = rule.textQuery?.text || rule.entityQuery?.entityId;
    const isSeen = ruleIdSet.has(getRuleHash(rule, query));
    if (isSeen) dupes.push([rule, idx]);
    else ruleIdSet.add(getRuleHash(rule, query));
  });
  return dupes;
});

export const selectLensEditorStatus = (st) => st.lensEditor.status;
export const selectLensEditorLoaded = (st) => st.lensEditor.loaded;

export const selectMatchingSubtypes = createSelector([selectAllFieldRules], (rules) =>
  distinct(rules.map((r) => r.subtype))
);
export const selectMatchingDescriptions = createSelector(
  [selectAllFieldRules, selectSelectedRuleTypeComponents],
  (rules, rtc) => distinct(rules.filter((rule) => rtc.subtypes.includes(rule.subtype)).map((r) => r.description))
);

export const selectSelectedRules = createSelector(
  [selectRules, (st) => st.lensEditor.selectedRuleIds],
  (rules, selectedIds) => rules.filter((r) => contains(selectedIds, r.ruleId))
);

export const selectIsRuleSelected = createSelector(
  [(st: RootState) => st.lensEditor.selectedRuleIds, (_state, ruleId) => ruleId],
  (ruleIds, ruleId: number) => ruleIds.indexOf(ruleId) !== -1
);

export const selectCountsByField: (st: RootState) => Map<number | undefined | null, number> = createSelector(
  [selectRules],
  (rules) =>
    new Map(
      Array.of(...groupBy(rules, (rule) => rule.fieldId).entries()).map(([fieldId, rules]) => [fieldId, rules.length])
    )
);
export const selectCountsBySubtype: (st: RootState) => Map<string, number> = createSelector(
  [selectAllFieldRules],
  (rules) =>
    new Map(
      Array.of(...groupBy(rules, (rule) => rule.subtype).entries()).map(([subtype, rules]) => [subtype, rules.length])
    )
);
export const selectCountsByDescription: (st: RootState) => Map<string, number> = createSelector(
  [selectAllFieldRules, selectSelectedRuleTypeComponents],
  (rules, { subtypes }) =>
    new Map(
      Array.of(
        ...groupBy(
          rules.filter((rule) => subtypes.includes(rule.subtype)),
          (rule) => rule.description
        ).entries()
      ).map(([desc, rules]) => [desc, rules.length])
    )
);

export const selectBrowserConcepts = (st: RootState) => st.lensEditor.browserConcepts;
export const selectToolTab = (st: RootState) => st.lensEditor.toolTab;

export const selectAllLensEditorReviewConfigs = (state: RootState): ReviewConfig[] => state.lensEditor.reviewConfigs;
export const selectLensEditorReviewConfigById = createSelector(
  [selectAllLensEditorReviewConfigs, (_state, id: number) => id],
  (configs, id) => configs.find((rc) => rc.id === id)
);

export const selectProfileBuilderFieldById = createSelector(
  [selectProfileBuilderFields, (_state, id: number | undefined) => id],
  (fields, id) => {
    if (id === undefined) {
      return undefined;
    }
    return fields.find((f) => f.id === id);
  }
);
export const selectProfileBuilderSectionById = createSelector(
  [selectProfileBuilderSections, (_state, id: number | undefined) => id],
  (sections, id) => {
    if (id === undefined) {
      return undefined;
    }
    return sections.find((s) => s.id === id);
  }
);

export const selectProfileBuilderCellsByFieldId = createSelector(
  [selectProfileBuilderFields, (_state, fieldId: number | undefined) => fieldId],
  (fields, fieldId) => {
    if (fieldId === undefined) {
      return [];
    } else {
      return fields.find((f) => f.id === fieldId)?.cells || [];
    }
  }
);

export const selectLensEditorAiFieldConfigById = createSelector(
  [selectLensEditorFieldAiConfigs, (_state, id: number) => id],
  (configs, id) => configs.find((rc) => rc.fieldId === id)
);

export const selectLensEditorFieldTagConfigById = createSelector(
  [selectLensEditorFieldTagConfigs, (_state, id: number) => id],
  (configs, id) => configs.find((rc) => rc.fieldId === id)
);

export const selectLensEditorAiCellConfigById = createSelector(
  [selectLensEditorCellAiConfigs, (_state, id: number | undefined) => id],
  (configs, id) => {
    if (!id) return undefined;
    return configs.find((c) => c.cellId === id);
  }
);

export const selectChronologyConfig = (state: RootState): LensChronologyConfigView | undefined =>
  state.lensEditor.chronologyConfig;

export const selectPageLabels = (state: RootState): PageLabel[] => state.lensEditor.pageLabels;
export const selectPageLabelRules = (state: RootState): PageLabelRule[] => {
  return state.lensEditor.pageLabelRules.slice(0).sort((a, b) => {
    return a.groupName && !b.groupName
      ? -1
      : !a.groupName && b.groupName
        ? 1
        : a.groupName && b.groupName
          ? a.groupName?.localeCompare(b.groupName)
          : 0;
  });
};
export const selectPageLabelsStatus = (state: RootState): RequestStatus => state.lensEditor.pageLabelsStatus;

export const selectPageLabelStats = (state: RootState): PageLabelStats[] => state.lensEditor.pageLabelsStats;
export const selectPageLabelStatsStatus = (state: RootState): RequestStatus => state.lensEditor.pageLabelsStatsStatus;
export const selectSystemPageLabelGroups = (state: RootState): SystemPageLabelGroupView[] =>
  state.lensEditor.systemPageLabelGroups;
export const selectAllSystemPageLabelGroups = (state: RootState): SystemPageLabelGroupView[] =>
  state.lensEditor.allSystemPageLabelGroups;

export const selectCustomPropertiesStatus = (state: RootState): RequestStatus =>
  state.lensEditor.customPropertiesStatus;
export const selectCaseProperties = (state: RootState): CustomCaseProperty[] => state.lensEditor.caseProperties;
export const selectDocumentProperties = (state: RootState): CustomDocumentProperty[] =>
  state.lensEditor.documentProperties;
export const selectCalcProperties = (state: RootState): CalcCaseProperty[] => state.lensEditor.calcProperties;
export const selectCaseDisplayGroups = (state: RootState): CaseDisplayGroup[] =>
  state.lensEditor.caseDisplayGroups.slice().sort((a, b) => a.name.localeCompare(b.name));

export const selectProfgenBatches = (state: RootState): Record<string, ProfgenBatchState> =>
  state.lensEditor.profgenBatches;

export const selectProfileBuilderAiMode = (state: RootState): boolean => state.lensEditor.profileBuilderAiMode;

export const selectProfileGenTestCases = (state: RootState) => state.lensEditor.profileGenTestCases;

export const selectProfileGenTestResults = (state: RootState) => state.lensEditor.profileGenTestResults;
export const selectLensDocumentStatus = (state: RootState) => state.lensEditor.lensDocumentStatus;
